import { Button, type ButtonOwnProps, SvgIcon, type SxProps, type Theme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { palette } from '../../assets/styles/themes/tokens';
import SubscribeIcon from '../../assets/subscription/subscribe.svg';
import { routes } from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import getMuiIcon from '../../utils/getMuiIcon';
import useRouter from '../../utils/useRouter';
import CustomBanner from '../CustomBanner/CustomBanner';
import Benefits from '../PricingPackage/Benefits';
import {
	getUpgradeBannerProps,
	PremiumBenefits,
} from '../ReaderBannerContainer/utils/upgradeBannerUtils';

interface OwnProps {
	productId: string;
	attachmentsCount: number;
	buttonSx?: SxProps<Theme>;
	size?: ButtonOwnProps['size'];
}

export const NewAttachmentsButton: React.FC<OwnProps> = ({
	attachmentsCount,
	productId,
	buttonSx,
	size,
}) => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const user = useSelector(selectUser);
	const [isBannerOpen, setIsBannerOpen] = useState(false);

	const handleButtonClick = () => {
		if (user?.hasDocumentLevelAccess) {
			router.push(`/${routes.productAttachmentsPage.root}/${productId}`);
		} else {
			setIsBannerOpen(true);
		}
	};

	const attachmentsText = attachmentsCount === 1 ? t('Attachment') : t('Attachments');

	if (!user) return null;

	const upgradeBannerProps = getUpgradeBannerProps(user, router, SubscribeIcon, t);
	const bannerProps = {
		...upgradeBannerProps,
		additionalInfo: <Benefits benefit={PremiumBenefits} />,
	};

	return (
		<>
			<Button
				onClick={handleButtonClick}
				variant="outlined"
				size={size || 'large'}
				color="primary"
				key="attachments-button"
				data-testid="attachments-button"
				type="button"
				aria-label={`${attachmentsCount} ${attachmentsText}`}
				startIcon={
					<SvgIcon
						component={getMuiIcon('paperclip')}
						sx={{ fill: palette.primary.main }}
						aria-hidden="true"
					/>
				}
				sx={{
					...buttonSx,
				}}
			>
				{attachmentsCount} {attachmentsText}
			</Button>
			<CustomBanner open={isBannerOpen} onClose={() => setIsBannerOpen(false)} {...bannerProps} />
		</>
	);
};
